import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ScenarioCard from './ScenarioCard';
import ScenarioConsultationDrawer from './ScenarioConsultationDrawer';
import ScenarioSelector from './ScenarioSelector';

interface ScenarioCardsSectionProps {
  onFiltersChange?: (filters: { industry: string; mode: string; goal: string }) => void;
}

export default function ScenarioCardsSection({ onFiltersChange }: ScenarioCardsSectionProps) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('');
  const [filters, setFilters] = useState({
    industry: '',
    mode: '',
    goal: ''
  });

  const handleConsultClick = (scenarioTitle: string) => {
    setSelectedScenario(scenarioTitle);
    setDrawerOpen(true);
  };

  const handleFilterChange = useCallback((newFilters: { industry: string; mode: string; goal: string }) => {
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  }, [onFiltersChange]);

  const scenarios = [
    {
      id: 'ecommerce-boutique',
      industry: 'ecommerce',
      mode: 'boutique',
      goals: ['tax-free', 'pre-tax', 'accounting'],
      number: '一',
      title: '跨境电商（9810）精品店',
      applicableFor: 'SKU相对集中、利润敏感、对口径稳定性要求较高的卖家',
      challenges: [
        '平台流水、收款、报关、发票口径难统一，解释成本高',
        '预退税后核销链路复杂，资料关系依赖人工经验',
        '单证与版本管理分散，抽查准备成本高',
      ],
      mechanism: '三流对齐标红 + 一票一链路固化 + 单证目录化归档',
      recommendations: '9810 订阅版（标准/尊享）+（可选）核算/退税支持',
      buttonText: '查看9810方案',
      onButtonClick: () => navigate('/capabilities#9810'),
      onConsultClick: () => handleConsultClick('跨境电商（9810）精品店'),
    },
    {
      id: 'ecommerce-bulk',
      industry: 'ecommerce',
      mode: 'bulk',
      goals: ['tax-free', 'support'],
      number: '二',
      title: '跨境电商（9810）铺货/店群',
      applicableFor: 'SKU/单量大、流程效率优先、团队人力紧张',
      challenges: [
        '人工录入与对账工作量随规模非线性增长',
        '缺项/异常导致退单返工频繁，复盘困难',
        '单证散落，批次追踪与资料定位成本高',
      ],
      mechanism: '自动生成→提交前校验→回执留痕→批次归档（规模化时固化链路）',
      recommendations: 'A类工具先跑通 → 规模化上9810订阅（标准/尊享）+（可选）值守保障',
      buttonText: '查看规模化方案',
      onButtonClick: () => navigate('/capabilities#9810'),
      onConsultClick: () => handleConsultClick('跨境电商（9810）铺货/店群'),
    },
    {
      id: 'ecommerce-initial',
      industry: 'ecommerce',
      mode: 'initial',
      goals: ['tax-free'],
      number: '三',
      title: '跨境电商（9810）初期验证闭环',
      applicableFor: '刚开始做9810、需要低成本验证流程闭环',
      challenges: [
        '不清楚资料清单与字段要求',
        '不熟悉回执与归档结构',
        '早期投入需可控',
      ],
      mechanism: '先用A类跑通一票→明确资料结构→再升级订阅沉淀留痕',
      recommendations: 'A类9.9/99 → 9810基础/标准（按规模）',
      buttonText: '先跑通一票',
      onButtonClick: () => navigate('/capabilities#9810'),
      onConsultClick: () => handleConsultClick('跨境电商（9810）初期验证闭环'),
    },
    {
      id: 'trade-0110',
      industry: 'trade',
      mode: '',
      goals: ['tax-refund', 'accounting'],
      number: '四',
      title: '外贸公司（0110）一般贸易',
      applicableFor: '多客户/多单证、部门协作复杂、材料链条长',
      challenges: [
        '材料版本多、分散，退单原因沉淀不足',
        '税务/关务/财务口径不一致导致反复沟通',
        '资料导出与归档缺统一结构',
      ],
      mechanism: '统一资料目录结构 + 提交前校验 + 退单留痕 + 可导出归档包',
      recommendations: '0110订阅（按主体/规模选）+（可选）退税/核算支持',
      buttonText: '查看0110方案',
      onButtonClick: () => navigate('/capabilities#0110'),
      onConsultClick: () => handleConsultClick('外贸公司（0110）一般贸易'),
    },
    {
      id: 'market-1039',
      industry: 'market',
      mode: '',
      goals: ['tax-free', 'accounting'],
      number: '五',
      title: '市场商户（1039）年度管理',
      applicableFor: '年度管理为主、多批次、资料跨度长',
      challenges: [
        '年度汇总时集中补材料压力大',
        '资料查找与对应关系复杂',
        '口径不统一导致反复整理',
      ],
      mechanism: '年度资料集中归集 + 批次/主体归档 + 结构化目录可导出',
      recommendations: '1039年度套餐（起步价）+（按需）增强服务',
      buttonText: '查看1039方案',
      onButtonClick: () => navigate('/capabilities#1039'),
      onConsultClick: () => handleConsultClick('市场商户（1039）年度管理'),
    },
    {
      id: 'mixed-business',
      industry: 'mixed',
      mode: '',
      goals: ['tax-free', 'tax-refund', 'accounting'],
      number: '六',
      title: '混合业务（9810+0110）',
      applicableFor: '同时经营多条出口业务线、口径易冲突',
      challenges: [
        '不同业务口径与资料结构不一致，团队重复劳动',
        '多主体/多系统对齐难，协作成本高',
      ],
      mechanism: '按业务线分Tab管理 + 统一归档方法论 + 权限与留痕统一',
      recommendations: '9810订阅 + 0110订阅 +（可选）核算支持',
      buttonText: '获取组合方案',
      onButtonClick: () => {
        setSelectedScenario('混合业务（9810+0110）');
        setDrawerOpen(true);
      },
      onConsultClick: () => handleConsultClick('混合业务（9810+0110）'),
    },
  ];

  // 筛选逻辑
  const filteredScenarios = scenarios.filter(scenario => {
    // 如果没有任何筛选条件，显示所有方案
    if (!filters.industry && !filters.mode && !filters.goal) {
      return true;
    }

    let matches = true;

    // 行业匹配
    if (filters.industry) {
      matches = matches && scenario.industry === filters.industry;
    }

    // 模式匹配（仅跨境电商有模式）
    if (filters.mode && filters.industry === 'ecommerce') {
      matches = matches && scenario.mode === filters.mode;
    }

    // 目标匹配
    if (filters.goal) {
      matches = matches && scenario.goals.includes(filters.goal);
    }

    return matches;
  });

  // 如果有筛选条件，只显示前3个匹配结果
  const displayScenarios = (filters.industry || filters.mode || filters.goal) 
    ? filteredScenarios.slice(0, 3)
    : scenarios;

  const hasMoreResults = filteredScenarios.length > 3 && (filters.industry || filters.mode || filters.goal);

  return (
    <>
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <ScenarioSelector onFilterChange={handleFilterChange} />
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* 筛选结果提示 */}
          {(filters.industry || filters.mode || filters.goal) && (
            <div className="mb-8 text-center">
              <p className="text-gray-600">
                为您匹配到 <span className="font-semibold text-teal-600">{filteredScenarios.length}</span> 个方案
                {displayScenarios.length < filteredScenarios.length && (
                  <span className="text-sm text-gray-500 ml-2">（显示前 {displayScenarios.length} 个）</span>
                )}
              </p>
            </div>
          )}

          {/* 方案卡片 */}
          {displayScenarios.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {displayScenarios.map((scenario) => (
                <ScenarioCard key={scenario.id} {...scenario} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                <i className="ri-search-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 mb-4">暂无匹配的方案</p>
              <button
                onClick={() => setFilters({ industry: '', mode: '', goal: '' })}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium cursor-pointer"
              >
                查看全部方案
              </button>
            </div>
          )}

          {/* 查看更多按钮 */}
          {hasMoreResults && (
            <div className="mt-12 text-center">
              <button
                onClick={() => setFilters({ industry: '', mode: '', goal: '' })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <span>查看全部 {filteredScenarios.length} 个匹配方案</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>
      
      <ScenarioConsultationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        scenarioName={selectedScenario}
      />
    </>
  );
}
